// Builds clean, text-based A4 PDF reports (selectable text, small file size)
// from a markdown report string plus an optional hitting-chart image. This
// replaces the previous approach of screenshotting the on-screen report with
// html2canvas, which produced large, blurry, raster-only PDFs.
import jsPDF from "jspdf";

const MM_PER_PT = 0.3528;

// Convert a (court / hitting chart) canvas element into a downscaled JPEG data
// URL plus its pixel dimensions. Downscaling + JPEG keeps the chart small in
// the final PDF. Returns null when the canvas is missing or tainted.
export function canvasToJpeg(canvas, maxWidth = 1400, quality = 0.82) {
  if (!canvas || !canvas.width || !canvas.height) {
    return null;
  }
  let w = canvas.width;
  let h = canvas.height;
  if (w > maxWidth) {
    h = Math.round((h * maxWidth) / w);
    w = maxWidth;
  }
  const off = document.createElement("canvas");
  off.width = w;
  off.height = h;
  const ctx = off.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(canvas, 0, 0, w, h);
  try {
    return { dataUrl: off.toDataURL("image/jpeg", quality), w, h };
  } catch (e) {
    return null;
  }
}

// Build a compressed, text-based A4 PDF document and return the jsPDF instance.
// The caller decides whether to .save() it or .output("blob") it for upload.
export function buildReportPdf({ title, subtitle, markdown, chart, chartCaption }) {
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentW = pageW - margin * 2;
  let y = margin;

  const ensure = (needed) => {
    if (y + needed > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // Render a paragraph of text with inline **bold** support and word wrapping.
  const renderRich = (
    text,
    { size = 10, bold = false, indent = 0, color = [30, 30, 30], gapAfter = 1.6 } = {}
  ) => {
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    const lineH = size * MM_PER_PT * 1.3;
    const left = margin + indent;
    const maxW = contentW - indent;

    // Split on the bold delimiter; odd-indexed fragments are bold.
    const fragments = String(text).split("**");
    const words = [];
    fragments.forEach((frag, fi) => {
      const isBold = bold || fi % 2 === 1;
      frag.split(/(\s+)/).forEach((tok) => {
        if (tok === "") return;
        words.push({ text: tok, bold: isBold, space: /^\s+$/.test(tok) });
      });
    });

    ensure(lineH);
    let x = left;
    const newline = () => {
      y += lineH;
      ensure(lineH);
      x = left;
    };
    for (const wd of words) {
      doc.setFont("helvetica", wd.bold ? "bold" : "normal");
      const width = doc.getTextWidth(wd.text);
      if (wd.space) {
        if (x > left) x += width; // collapse leading spaces on a new line
        continue;
      }
      if (x + width > left + maxW && x > left) {
        newline();
      }
      doc.text(wd.text, x, y);
      x += width;
    }
    y += lineH + gapAfter;
  };

  const hr = () => {
    ensure(3);
    doc.setDrawColor(205, 205, 205);
    doc.line(margin, y, pageW - margin, y);
    y += 3;
  };

  // Strip markdown artefacts we don't render specially.
  const clean = (s) => String(s).replace(/`([^`]*)`/g, "$1");

  if (title) renderRich(title, { size: 18, bold: true, gapAfter: 1 });
  if (subtitle) {
    renderRich(subtitle, { size: 11, color: [110, 110, 110], gapAfter: 2 });
  }
  if (title || subtitle) hr();

  const lines = String(markdown || "").split("\n");
  for (const raw of lines) {
    const tr = raw.trim();
    if (tr === "") {
      y += 1.5;
      continue;
    }
    // Markdown table row.
    if (/^\|.*\|$/.test(tr)) {
      const compact = tr.replace(/\s/g, "");
      if (/^\|[:\-|]+\|$/.test(compact)) continue; // header separator row
      const cells = tr
        .split("|")
        .slice(1, -1)
        .map((c) => clean(c.trim()));
      renderRich(cells.join("   |   "), { size: 9, indent: 2, gapAfter: 0.6 });
      continue;
    }
    if (tr.startsWith("### ")) {
      ensure(6);
      renderRich(clean(tr.slice(4)), { size: 12, bold: true, gapAfter: 1 });
      continue;
    }
    if (tr.startsWith("## ")) {
      ensure(7);
      renderRich(clean(tr.slice(3)), { size: 14, bold: true, gapAfter: 1.4 });
      continue;
    }
    if (tr.startsWith("# ")) {
      ensure(8);
      renderRich(clean(tr.slice(2)), { size: 16, bold: true, gapAfter: 1.6 });
      continue;
    }
    let m = tr.match(/^[-*]\s+(.*)$/);
    if (m) {
      renderRich("•  " + clean(m[1]), { size: 10, indent: 4, gapAfter: 0.8 });
      continue;
    }
    m = tr.match(/^(\d+)\.\s+(.*)$/);
    if (m) {
      renderRich(m[1] + ".  " + clean(m[2]), { size: 10, indent: 4, gapAfter: 0.8 });
      continue;
    }
    renderRich(clean(tr), { size: 10, gapAfter: 1.6 });
  }

  // Embed the hitting chart image, on a fresh page if it would not fit.
  if (chart && chart.dataUrl) {
    const imgW = contentW;
    const imgH = (chart.h * imgW) / chart.w;
    const captionH = chartCaption ? 8 : 0;
    if (y + captionH + Math.min(imgH, pageH - margin * 2 - captionH) > pageH - margin) {
      doc.addPage();
      y = margin;
    }
    if (chartCaption) {
      renderRich(chartCaption, { size: 12, bold: true, gapAfter: 1.4 });
    }
    let drawW = imgW;
    let drawH = imgH;
    const maxH = pageH - margin - y;
    if (drawH > maxH) {
      drawH = maxH;
      drawW = (chart.w * drawH) / chart.h;
    }
    doc.addImage(chart.dataUrl, "JPEG", margin, y, drawW, drawH);
    y += drawH + 2;
  }

  return doc;
}

// Return just the base64 payload (no data-URI prefix) of a jsPDF document, for
// storing the PDF in Firestore.
export function pdfToBase64(doc) {
  const uri = doc.output("datauristring");
  const comma = uri.indexOf(",");
  return comma >= 0 ? uri.slice(comma + 1) : uri;
}

// Turn a stored base64 PDF back into an object URL for viewing / download.
export function base64ToBlobUrl(base64, type = "application/pdf") {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) {
    bytes[i] = bin.charCodeAt(i);
  }
  return URL.createObjectURL(new Blob([bytes], { type }));
}
